import { useState } from 'react';
import loadable from '@loadable/component';

import { Box, Grid, Text, useTheme } from '@simplywallst/ui-core';
import RewardSolid from '@simplywallst/ui-core/icons/RewardSolid';
import WarningSolid from '@simplywallst/ui-core/icons/WarningSolid';

import type { Portfolio } from '@/hooks/portfolio/usePortfolio';

const RisksAndRewardsModal = loadable(
  () => import('./components/RisksAndRewardsModal'),
  {
    resolveComponent: (c) => c.RisksAndRewardsModal,
  }
);

interface Props {
  portfolio: Portfolio;
}

const getCount = (count: number | null) => {
  if (count === null) return '0';
  if (count > 99) return '+99';
  return String(count);
};

export const RisksAndRewards = ({ portfolio }: Props) => {
  const theme = useTheme();
  const [risksOrRewards, setRisksOrRewards] = useState<
    'risks' | 'rewards' | null
  >(null);

  const { risksCount, rewardsCount } = portfolio.analysis;

  const hasRisks = risksCount !== null;
  const hasRewards = rewardsCount !== null;

  const showRisksAndRewardsModal = risksOrRewards !== null;

  return (
    <Grid templateColumns="auto auto" gap="8px">
      {hasRisks && (
        <button onClick={() => setRisksOrRewards('risks')}>
          <Box borderRadius="x0_5" backgroundColor="badSubtle">
            <Grid
              padding="x0_5"
              paddingRight="x1_5"
              paddingLeft="x1"
              borderRadius="x0_5"
              templateColumns="max-content max-content"
              alignItems="center"
              gap="2px"
              data-cy-id="portfolio-risks"
              data-testid="portfolio-risks"
            >
              <Box position="relative" width={24} height={24}>
                <Box
                  position="absolute"
                  backgroundColor="white"
                  borderRadius="x3"
                  width={12}
                  height={12}
                  transform="translate(50%, 50%)"
                />
                <Box position="relative">
                  <WarningSolid fill={theme.color.bad} />
                </Box>
              </Box>
              <Text typography="xSmall">
                {portfolio.isAnalysisRestricted ? '-' : getCount(risksCount)}
              </Text>
            </Grid>
          </Box>
        </button>
      )}
      {hasRewards && (
        <button onClick={() => setRisksOrRewards('rewards')}>
          <Box borderRadius="x0_5" backgroundColor="goodSubtle">
            <Grid
              padding="x0_5"
              paddingRight="x1_5"
              paddingLeft="x1"
              borderRadius="x0_5"
              templateColumns="max-content max-content"
              alignItems="center"
              data-cy-id="portfolio-rewards"
              data-testid="portfolio-rewards"
            >
              <RewardSolid fill={theme.color.good} />
              <Text typography="xSmall">
                {portfolio.isAnalysisRestricted ? '-' : getCount(rewardsCount)}
              </Text>
            </Grid>
          </Box>
        </button>
      )}
      {showRisksAndRewardsModal && (
        <RisksAndRewardsModal
          portfolio={portfolio}
          selected={risksOrRewards}
          onClose={() => setRisksOrRewards(null)}
        />
      )}
    </Grid>
  );
};
